// src/services/nodeManagementApi.js

import * as api from './api';

/**
 * Get or generate an invitation code for the user
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {string} nodeReferenceCode - Optional node reference code
 * @returns {Promise<Object>} Response with invitation code data
 */
export const getOrGenerateInvitationCode = async (walletAddress, blockchainNetworkId, walletType, nodeReferenceCode) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // First try to get existing code
    const existingCodeResponse = await api.getReferrals(
      walletAddress,
      blockchainNetworkId,
      signature,
      messageResponse.message,
      walletType
    );
    
    if (existingCodeResponse && 
        existingCodeResponse.success && 
        existingCodeResponse.data && 
        existingCodeResponse.data.has_invitation_code) {
      return {
        success: true,
        invitationCode: existingCodeResponse.data.invitation_code
      };
    }
    
    // If no existing code, generate a new one
    const newCodeResponse = await api.generateInvitationCode(
      walletAddress,
      blockchainNetworkId,
      signature,
      messageResponse.message,
      walletType,
      nodeReferenceCode
    );
    
    if (newCodeResponse && newCodeResponse.success && newCodeResponse.data) {
      return {
        success: true,
        invitationCode: newCodeResponse.data.invitation_code
      };
    }
    
    throw new Error(newCodeResponse?.message || 'Failed to generate invitation code');
  } catch (error) {
    console.error('Error in getOrGenerateInvitationCode:', error);
    return {
      success: false,
      message: error.message || 'Failed to get or generate invitation code'
    };
  }
};

/**
 * Check if a user is already bound to a superior node
 * Modified to use existing APIs rather than a new endpoint
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @returns {Promise<Object>} Response with binding status
 */
export const checkNodeRelationshipStatus = async (walletAddress, blockchainNetworkId, walletType) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // We'll use the getWalletApplications API to check if user has active nodes
    const applicationsResponse = await api.getWalletApplications(
      walletAddress,
      signature,
      messageResponse.message,
      blockchainNetworkId,
      walletType
    );
    
    // Check if any of the applications have a superior_binding property
    let isBound = false;
    let bindingData = null;

    if (applicationsResponse && applicationsResponse.applications) {
      // Find applications that are active and have superior binding
      const activeApps = applicationsResponse.applications.filter(app => 
        (app.status === 'active' || app.status === 'approved')
      );

      for (const app of activeApps) {
        if (app.superior_binding === true || app.has_superior_node === true || app.superior_node_id) {
          isBound = true;
          bindingData = {
            node_reference_code: app.reference_code,
            superior_node_reference: app.superior_node_reference || null,
            superior_node_id: app.superior_node_id || null
          };
          break;
        }
      }
    }
    
    return {
      success: true,
      isBound: isBound,
      data: bindingData
    };
  } catch (error) {
    console.error('Error in checkNodeRelationshipStatus:', error);
    return {
      success: false,
      isBound: false,
      message: error.message || 'Failed to check relationship status'
    };
  }
};

/**
 * Check relationship status for a specific node
 * @param {number} nodeId - The ID of the node to check
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @returns {Promise<Object>} Response with relationship status
 */
export const getNodeRelationshipStatus = async (nodeId, blockchainNetworkId) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    console.log("Checking relationship status for node:", nodeId, "with network ID:", blockchainNetworkId);
    
    // Call the API endpoint
    const response = await fetch('https://api.aeronyx.network/api/nodes/relationship/status/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        node_id: nodeId,
        blockchain_network_id: blockchainNetworkId
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to check node relationship status');
    }
    
    return {
      success: true,
      isBound: result.data?.isBound || false,
      relationshipData: result.data?.relationshipData || null
    };
  } catch (error) {
    console.error('Error in getNodeRelationshipStatus:', error);
    return {
      success: false,
      isBound: false,
      message: error.message || 'Failed to check node relationship status'
    };
  }
}


/**
 * Get the downline network for a specific node
 * Updated to use the node-downline endpoint from API docs
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {number} nodeId - ID of the node to get downline for
 * @param {boolean} includeIndirect - Whether to include indirect relationships
 * @param {number} page - Page number
 * @param {number} pageSize - Page size
 * @param {string} sortField - Field to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Promise<Object>} Response with downline data
 */
export const getNodeDownlineNetwork = async (
  walletAddress, 
  blockchainNetworkId, 
  walletType, 
  nodeId,
  includeIndirect = true, 
  page = 1, 
  pageSize = 10,
  sortField = 'created_at',
  sortDirection = 'desc'
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Use the node-downline endpoint as per API documentation
    const response = await fetch('https://api.aeronyx.network/api/nodes/node-downline/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType,
        node_id: nodeId,
        include_indirect: includeIndirect,
        page: page,
        page_size: pageSize,
        sort_field: sortField,
        sort_direction: sortDirection
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch node downline data');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNodeDownlineNetwork:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch node downline data'
    };
  }
}

/**
 * Bind a specific node to a superior node using the node relationship API
 * @param {string} superiorNodeReference - Reference code of the superior node
 * @param {string|number} subordinateNodeIdOrWalletAddress - ID of the subordinate node or wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {boolean} useNodeId - Whether to use node ID instead of wallet address
 * @returns {Promise<Object>} Response with result
 */
export const bindToSuperiorNode = async (
  superiorNodeReference, 
  subordinateNodeIdOrWalletAddress, 
  blockchainNetworkId, 
  walletType,
  useNodeId = false
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    console.log("Binding node to superior with network ID:", blockchainNetworkId);
    
    // Get wallet address for signature (either directly or from node)
    let walletAddress;
    if (useNodeId) {
      // Get wallet address from node ID
      try {
        const nodeInfo = await getNodeDetails(subordinateNodeIdOrWalletAddress, blockchainNetworkId);
        if (!nodeInfo.success || !nodeInfo.data || !nodeInfo.data.wallet_address) {
          throw new Error('Could not retrieve wallet address for the selected node');
        }
        walletAddress = nodeInfo.data.wallet_address;
      } catch (err) {
        console.error('Error getting node wallet address:', err);
        throw new Error('Failed to get wallet address for the selected node');
      }
    } else {
      walletAddress = subordinateNodeIdOrWalletAddress;
    }

    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Prepare the request body based on whether we're using node ID or wallet address
    const requestBody = {
      superior_node_reference: superiorNodeReference,
      blockchain_network_id: blockchainNetworkId,
      signature: signature,
      message: messageResponse.message,
      wallet_type: walletType
    };
    
    // Add either subordinate_node_id or subordinate_wallet_address based on useNodeId flag
    if (useNodeId) {
      requestBody.subordinate_node_id = subordinateNodeIdOrWalletAddress;
    } else {
      requestBody.subordinate_wallet_address = walletAddress;
    }
    
    console.log("Binding with request body:", { ...requestBody, signature: "REDACTED" });
    
    // Use the correct node relationship creation API
    const response = await fetch('https://api.aeronyx.network/api/nodes/relationships/create/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || result.errors?.general?.[0] || 'Failed to bind to superior node');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in bindToSuperiorNode:', error);
    return {
      success: false,
      message: error.message || 'Failed to bind to superior node'
    };
  }
};


/**
 * Generate an invitation code for a specific node
 * Updated to use the generate-for-node endpoint as per API docs
 * @param {string} walletAddress - User's wallet address for authentication
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {number} nodeId - ID of the node to generate code for
 * @returns {Promise<Object>} Response with invitation code data
 */
export const getNodeInvitationCode = async (walletAddress, blockchainNetworkId, walletType, nodeId) => {
  try {
    console.log("Getting node invitation code for:", nodeId);
    
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Use the generate-for-node endpoint as per API documentation
    try {
      const response = await fetch('https://api.aeronyx.network/api/invitation-code/generate-for-node/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_id: nodeId,
          wallet_address: walletAddress,
          blockchain_network_id: blockchainNetworkId,
          signature: signature,
          message: messageResponse.message,
          wallet_type: walletType
        }),
      });
      
      const result = await response.json();
      
      if (result.success && result.data && result.data.invitation_code) {
        // Successfully got a node-specific code
        return {
          success: true,
          invitationCode: result.data.invitation_code,
          isNew: result.data.is_new || false,
          nodeReferenceCode: result.data.node_reference_code,
          totalReferrals: result.data.total_referrals || 0,
          isNodeSpecific: true
        };
      } else {
        throw new Error(result.message || 'Failed to generate node invitation code');
      }
    } catch (nodeCodeErr) {
      console.warn("Node invitation code API error:", nodeCodeErr);
      
      // Fall back to trying alternative methods if the primary one fails
      // First try to get existing wallet-level code as fallback
      try {
        const existingCodeResponse = await api.getReferrals(
          walletAddress,
          blockchainNetworkId,
          signature,
          messageResponse.message,
          walletType
        );
        
        if (existingCodeResponse && 
            existingCodeResponse.success && 
            existingCodeResponse.data && 
            existingCodeResponse.data.has_invitation_code) {
          
          // We found a wallet-level invitation code, not node-specific
          return {
            success: true,
            invitationCode: existingCodeResponse.data.invitation_code,
            isNew: false,
            nodeReferenceCode: null,
            totalReferrals: existingCodeResponse.data.total_referrals || 0,
            isNodeSpecific: false  // This is a wallet-level code, not node-specific
          };
        }
      } catch (err) {
        console.warn("Failed to fetch wallet invitation code, attempting to generate new code", err);
      }
      
      // As last resort, generate a new wallet-level code
      try {
        const newCodeResponse = await api.generateInvitationCode(
          walletAddress,
          blockchainNetworkId,
          signature,
          messageResponse.message,
          walletType
        );
        
        if (newCodeResponse && newCodeResponse.success && newCodeResponse.data) {
          // This is a newly generated wallet-level code (not node-specific)
          return {
            success: true,
            invitationCode: newCodeResponse.data.invitation_code,
            isNew: true,
            nodeReferenceCode: null,
            totalReferrals: 0,
            isNodeSpecific: false // This is a wallet-level code, not node-specific
          };
        }
      } catch (err) {
        console.error("Failed to generate invitation code", err);
      }
      
      throw nodeCodeErr;
    }
  } catch (error) {
    console.error('Error in getNodeInvitationCode:', error);
    return {
      success: false,
      message: error.message || 'Failed to get or generate invitation code for this node'
    };
  }
};

/**
 * Get details for a specific node
 * @param {number} nodeId - ID of the node
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @returns {Promise<Object>} Node details
 */
export const getNodeDetails = async (nodeId, blockchainNetworkId) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Call the node details endpoint
    const response = await fetch(`https://api.aeronyx.network/api/nodes/${nodeId}/details/`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch node details');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNodeDetails:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch node details'
    };
  }
};

/**
 * Get the user's downline network data
 * Updated to use node-downline endpoint according to API docs
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {boolean} includeIndirect - Whether to include indirect relationships
 * @param {number} page - Page number
 * @param {number} pageSize - Page size
 * @param {string} nodeReferenceCode - Optional node reference code to filter by
 * @param {string} sortField - Field to sort by
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Promise<Object>} Response with downline data
 */
export const getDownlineNetwork = async (
  walletAddress, 
  blockchainNetworkId, 
  walletType, 
  includeIndirect = true, 
  page = 1, 
  pageSize = 10,
  nodeReferenceCode = null,
  sortField = 'created_at',
  sortDirection = 'desc'
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Use the node-downline endpoint as per API documentation
    try {
      const response = await fetch('https://api.aeronyx.network/api/nodes/node-downline/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wallet_address: walletAddress,
          blockchain_network_id: blockchainNetworkId,
          signature: signature,
          message: messageResponse.message,
          wallet_type: walletType,
          include_indirect: includeIndirect,
          page: page,
          page_size: pageSize,
          node_reference_code: nodeReferenceCode,
          sort_field: sortField,
          sort_direction: sortDirection
        }),
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch downline data');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (err) {
      console.error("Error calling node-downline API:", err);
      
      // Fallback to the referrals API if node-downline fails
      console.warn('Downline API unavailable, falling back to referrals API');
      
      // Use the referrals API if the downline API is not available
      const result = await api.getReferrals(
        walletAddress,
        blockchainNetworkId,
        signature,
        messageResponse.message,
        walletType
      );
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch referrals data');
      }
      
      // Transform the referrals data to match expected downline format
      const transformedData = {
        subordinates: result.data?.referrals?.map(ref => ({
          id: ref.id,
          reference_code: ref.reference_code || 'N/A',
          full_name: ref.full_name || null,
          tier_name: ref.tier || 'Standard',
          status: ref.status || 'active',
          relationship_level: ref.is_direct ? 1 : 2,
          created_at: ref.created_at,
          total_commissions: ref.commissions || 0
        })) || [],
        network_statistics: {
          direct_subordinates: result.data?.referrals?.filter(ref => ref.is_direct).length || 0,
          total_network_size: result.data?.referrals?.length || 0,
          total_commissions: result.data?.total_commissions || 0
        },
        pagination: {
          current_page: 1,
          total_pages: 1,
          total_items: result.data?.referrals?.length || 0,
          page_size: pageSize
        }
      };
      
      return {
        success: true,
        data: transformedData
      };
    }
  } catch (error) {
    console.error('Error in getDownlineNetwork:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch downline data'
    };
  }
};

/**
 * Check the user's referral status
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<Object>} Response with referral status
 */
export const checkReferralStatus = async (walletAddress) => {
  try {
    const response = await api.checkReferralStatus(walletAddress);
    return response;
  } catch (error) {
    console.error('Error in checkReferralStatus:', error);
    return {
      success: false,
      message: error.message || 'Failed to check referral status'
    };
  }
};

/**
 * Get network stability fees for a node
 * @param {string} walletAddress - User's wallet address for authentication
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {Object} options - Query options (page, pageSize, startDate, endDate)
 * @returns {Promise<Object>} Response with network stability fees
 */
export const getNetworkStabilityFees = async (
  walletAddress,
  blockchainNetworkId,
  walletType,
  options = {}
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Extract pagination and date range options
    const { 
      page = 1, 
      pageSize = 20, 
      startDate = null, 
      endDate = null 
    } = options;
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Prepare request parameters
    const requestParams = {
      wallet_address: walletAddress,
      blockchain_network_id: blockchainNetworkId,
      signature: signature,
      message: messageResponse.message,
      wallet_type: walletType,
      page: page,
      page_size: pageSize
    };
    
    // Add optional date filters if provided
    if (startDate) requestParams.start_date = startDate;
    if (endDate) requestParams.end_date = endDate;
    
    // Use the network-stability-fees endpoint as per API documentation
    const response = await fetch('https://api.aeronyx.network/api/points/network-stability-fees/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestParams),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch network stability fees');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNetworkStabilityFees:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch network stability fees'
    };
  }
};

/**
 * Get point distributions for a node
 * @param {string} walletAddress - User's wallet address for authentication
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {number} nodeId - ID of the node
 * @param {string} period - Time period (7d, 30d, 90d, all)
 * @returns {Promise<Object>} Response with point distributions
 */
export const getPointDistributions = async (
  walletAddress,
  blockchainNetworkId,
  walletType,
  nodeId,
  period = 'all'
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Use the team-points endpoint to get point distributions as per API documentation
    const response = await fetch('https://api.aeronyx.network/api/nodes/team-points/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType,
        node_id: nodeId,
        period: period
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch point distributions');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getPointDistributions:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch point distributions'
    };
  }
};

/**
 * Get referrals for a specific node
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - User's wallet type
 * @param {number} nodeId - ID of the node to get referrals for
 * @param {number} page - Page number
 * @param {number} pageSize - Page size
 * @param {Object} period - Optional time period filter (start_date, end_date)
 * @returns {Promise<Object>} Response with referrals data
 */
export const getNodeReferrals = async (
  walletAddress,
  blockchainNetworkId,
  walletType,
  nodeId,
  page = 1,
  pageSize = 10,
  period = null
) => {
  try {
    // Ensure blockchainNetworkId is a number
    blockchainNetworkId = parseInt(blockchainNetworkId);
    if (isNaN(blockchainNetworkId)) blockchainNetworkId = 1; // Default to 1 (ethereum)
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Prepare request parameters
    const requestParams = {
      wallet_address: walletAddress,
      blockchain_network_id: blockchainNetworkId,
      signature: signature,
      message: messageResponse.message,
      wallet_type: walletType,
      node_id: nodeId,
      page: page,
      page_size: pageSize
    };
    
    // Add period filter if provided
    if (period) {
      Object.assign(requestParams, period);
    }
    
    // Use the nodes/referrals endpoint to get node referrals
    const response = await fetch('https://api.aeronyx.network/api/nodes/referrals/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestParams),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch node referrals');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNodeReferrals:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch node referrals'
    };
  }
};

// For backward compatibility, aliasing functions
export const getNodeDownline = getDownlineNetwork;

export default {
  getOrGenerateInvitationCode,
  bindToSuperiorNode,
  getDownlineNetwork,
  checkNodeRelationshipStatus,
  getNodeRelationshipStatus,
  getNodeDetails,
  checkReferralStatus,
  getNodeInvitationCode,
  getNodeDownline,
  getNodeDownlineNetwork,
  getNetworkStabilityFees,
  getPointDistributions,
  getNodeReferrals
};
